import React from 'react';
import { createUseStyles } from 'react-jss';
import type { Finding } from 'shared/lib/widgetCode/normalizeWidgetCode';
import useCopyText from './useCopyText';
import { LEVEL_COLOR, T } from './tokens';

export type FindingAction = { label: string; onClick: () => void; busy?: boolean };

type Props = {
  /** What Genesis changed (proposals only). */
  fixes?: string[];
  findings: Finding[];
  fixRequest: string;
  /** Buttons offered next to particular findings, by finding id. */
  actions?: Record<string, FindingAction>;
  /** Shown when there is nothing to report. */
  emptyText?: string;
};

const useStyles = createUseStyles({
  root: { display: 'grid', gap: 16, color: T.fg, fontSize: 13, lineHeight: 1.5 },
  title: { fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.muted, margin: '0 0 6px' },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 },
  item: { display: 'grid', gridTemplateColumns: '14px 1fr', gap: 8, alignItems: 'start' },
  lamp: { width: 8, height: 8, borderRadius: '50%', marginTop: 6 },
  check: { color: T.green, fontWeight: 700, lineHeight: '20px' },
  detail: { color: T.muted },
  items: { margin: '4px 0 0', padding: 0, listStyle: 'none', fontFamily: T.mono, fontSize: 11, color: T.muted, wordBreak: 'break-all' },
  action: {
    marginTop: 6,
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 980,
    border: `1px solid ${T.fieldLine}`,
    background: T.field,
    color: T.fg,
    cursor: 'pointer',
    '&:disabled': { opacity: 0.6, cursor: 'default' }
  },
  request: { border: `1px solid ${T.line}`, borderRadius: 8, background: T.chrome, padding: 12 },
  requestHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 8 },
  requestText: { margin: 0, whiteSpace: 'pre-wrap', fontFamily: T.mono, fontSize: 11, color: T.fg, maxHeight: 160, overflow: 'auto' },
  empty: { display: 'flex', gap: 8, alignItems: 'center', color: T.green, fontWeight: 600 }
});

const PasteReport: React.FC<Props> = ({ fixes = [], findings, fixRequest, actions = {}, emptyText }) => {
  const c = useStyles();
  const { copy, copied } = useCopyText();

  if (!fixes.length && !findings.length && emptyText) {
    return <div className={c.empty} data-testid="paste-report-empty"><span className={c.lamp} style={{ background: T.green }} />{emptyText}</div>;
  }

  return (
    <div className={c.root} data-testid="paste-report">
      {fixes.length > 0 && (
        <section>
          <h4 className={c.title}>Genesis fixed</h4>
          <ul className={c.list}>
            {fixes.map(fix => (
              <li key={fix} className={c.item}><span className={c.check} aria-hidden>✓</span><span>{fix}</span></li>
            ))}
          </ul>
        </section>
      )}
      {findings.length > 0 && (
        <section>
          <h4 className={c.title}>Check these</h4>
          <ul className={c.list}>
            {findings.map(f => (
              <li key={f.id} className={c.item} data-finding={f.id}>
                <span className={c.lamp} style={{ background: LEVEL_COLOR[f.level] }} title={f.level} />
                <span>
                  <strong>{f.title}</strong>
                  {f.detail && <span className={c.detail}> {f.detail}</span>}
                  {f.items && f.items.length > 0 && (
                    <ul className={c.items}>
                      {f.items.slice(0, 6).map(item => <li key={item}>{item.length > 120 ? `${item.slice(0, 117)}…` : item}</li>)}
                      {f.items.length > 6 && <li>and {f.items.length - 6} more</li>}
                    </ul>
                  )}
                  {actions[f.id] && (
                    <div>
                      <button type="button" className={c.action} onClick={actions[f.id].onClick} disabled={actions[f.id].busy}>
                        {actions[f.id].label}
                      </button>
                    </div>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {fixRequest && (
        <section className={c.request}>
          <div className={c.requestHead}>
            <h4 className={c.title} style={{ margin: 0 }}>Fix request for your AI tool</h4>
            <button type="button" className={c.action} style={{ marginTop: 0 }} onClick={() => copy('fix', fixRequest)}>
              {copied === 'fix' ? 'Copied' : 'Copy'}
            </button>
          </div>
          <pre className={c.requestText}>{fixRequest}</pre>
        </section>
      )}
    </div>
  );
};

export default PasteReport;
